// 서버 `/ws/viewer` 연결 하나를 **여러 원격 세션(RtcClient)이 나눠 쓰는** 다중화기.
//
// ## 왜 필요한가 — 탭으로 여러 기기를 동시에 열기(2026-09-12 사용자 요청)
//
// 종전 서버는 로그인 사용자당 뷰어 소켓을 **하나만** 두어(새 소켓이 오면 이전 것을 닫음) 세션마다 소켓을 열면
// **두 번째 탭을 여는 순간 첫 탭의 소켓이 끊겼다.** ⇒ **한 문서(창) 안의 로그인 세션은 소켓 하나를 공유**하고,
// 서버가 모든 세션 메시지에 싣는 `sessionId` 로 주인에게만 나눠 준다.
//
// 창이 다르면 문서가 달라 공유할 수 없다 → 창마다 소켓 하나. 2026-10-20 부터 서버는 사용자당 소켓을 **여러 개**(상한 16) 두고
// 메시지를 모든 소켓에 보낸다(다른 창의 세션 메시지는 아래 "먼저 온 메시지 보관" 이 잠시 들고 있다 버린다).
// 창이 닫혔을 때 그 창의 세션만 정리되도록 `claim` 때 `session_bind` 를 보낸다.
// 상한을 넘겨 밀려나면 서버가 `replaced` 를 알리고, 이 창은 **다시 붙지 않는다**(`REPLACED_ERROR`).
//
// 익명 접속은 세션마다 토큰이 달라(= 서버 키도 다름) 자연히 세션당 소켓 하나가 된다 — 같은 코드 경로를 탄다.
//
// ⚠️ 공유 단위는 **토큰이 아니라 사용자**다(`USER_SOCKET_KEY` 주석 — 토큰을 키로 쓰다 실기에서 세션이 끊겼다).
//
// ## 먼저 온 메시지 보관
//
// 등록 흐름은 "소켓 연결 → 접속 요청(REST) → 응답의 sessionId 로 등록" 순서라, 상대가 아주 빨리 승인하면 Offer 가
// **등록보다 먼저** 도착할 수 있다. 주인이 없는 세션 메시지는 잠시 보관했다가 등록(`claim`) 순간 넘겨준다.
//
// ## 끊기면 다시 붙는다(2026-10-20) — `onLost` 주석
// 서버 재시작·순간 끊김에도 화면(P2P)은 살아 있으므로 유예 안에 다시 붙어 세션을 이어 간다(등록 = 재접속 표 · 익명 = 처음 토큰).
//
// 💡 서버 주소(`wsBase`)는 쓰는 쪽(rtc.ts)이 넣는다 — 이 파일이 `config.ts`(브라우저 전용 값)를 불러오지 않아야 Node 에서 시험된다.

/** 세션 메시지 처리기(서버 원문 JSON). */
type Handler = (msg: any) => void;

/** 주인 없는 세션 메시지 보관 한도 — 오래된 것부터 버린다(등록은 보통 수백 ms 안에 일어난다). */
const UNCLAIMED_MAX = 64;
/** 주인 없는 세션 메시지 보관 시간(ms). 이보다 오래된 것은 다른 창/지난 세션의 잔재로 보고 버린다. */
const UNCLAIMED_TTL_MS = 30_000;
/** 세션 소유 표시 메시지 종류 — 🔴 서버 `WS_MESSAGE.SESSION_BIND` 와 같아야 한다. */
const SESSION_BIND_TYPE = 'session_bind';
/** 서버 메시지 종류 — 🔴 서버 `WS_MESSAGE` 의 VIEWER_ONLINE·VIEWER_RESUME·ERROR 와 같아야 한다. */
const MSG_VIEWER_ONLINE = 'viewer_online';
const MSG_VIEWER_RESUME = 'viewer_resume';
const MSG_ERROR = 'error';
/**
 * 서버가 이 사유를 알리고 닫으면 **다시 붙지 않는다** — 🔴 서버 `VIEWER_WS_REJECT` 값 + 레이트리밋 종료(`rate_limited`).
 * 계정 차단·비밀번호 초기화로 끊긴 창이 재접속을 반복하면 안 된다(어차피 거부되지만 서버를 두드린다).
 */
const FINAL_ERRORS = new Set(['unauthorized', 'account_disabled', 'credentials_reset', 'rate_limited', 'replaced']);
/**
 * 서버 사유 `replaced` — 같은 사용자의 원격 접속 창이 서버 상한(기본 16)을 넘어 가장 오래된 이 창이 밀려났다
 * (🔴 서버 `VIEWER_WS_REJECT.REPLACED`). 다시 붙으면 이번엔 다른 창을 밀어내 서로 끊어 대므로 붙지 않는다.
 */
export const REPLACED_ERROR = 'replaced';
/** 끊김 안내 문구 — 기본(네트워크·서버)과 밀려남을 구분한다. */
const LOST_TEXT_DEFAULT = '서버 연결이 끊겼습니다';
const LOST_TEXT_REPLACED = '원격 접속 창이 너무 많아 가장 오래된 이 창의 연결이 끊겼습니다';

/** 끊김 사유(서버가 알린 오류 코드, 없으면 빈 문자열) → 사용자 안내 문구. */
export function lostText(reason: string): string {
  return reason === REPLACED_ERROR ? LOST_TEXT_REPLACED : LOST_TEXT_DEFAULT;
}
/**
 * 끊긴 뒤 다시 붙기를 시도하는 시간(ms) — 🔴 서버 등록 뷰어 유예(`DEFAULT_SESSION_VIEWER_GONE_GRACE_MS` 30초)보다 짧게.
 * 유예가 끝난 뒤 붙으면 세션은 이미 정리됐다. (익명 유예는 15초라 그 안에 붙지 못하면 익명 세션은 정리된다.)
 */
const RECONNECT_WINDOW_MS = 25_000;
/** 재시도 간격(ms) — 순간 끊김은 빨리, 서버 재시작은 몇 초 걸리므로 차츰 늘린다(마지막 값을 반복). */
const RECONNECT_DELAYS_MS = [300, 1000, 2000, 3000, 5000];

/**
 * 로그인 사용자의 공유 소켓 키.
 *
 * 🔴 **토큰 문자열을 키로 쓰면 안 된다**(2026-09-14 실기에서 밟았다). 접근 토큰은 15분마다 바뀌는데
 * (재로그인·갱신), 토큰이 키면 **새 토큰 = 새 소켓**이 되고 서버는 사용자당 소켓 하나만 두므로
 * `ViewerPresenceService.register` 가 **멀쩡히 붙어 있던 이전 소켓을 닫는다** →
 * 진행 중이던 원격 세션이 "서버 연결이 끊겼습니다" 로 죽었다.
 * 이 문서 안에서 로그인 사용자는 한 명이므로 키도 하나면 충분하다.
 *
 * ⚠️ 익명 접속은 **세션마다 토큰이 달라 서버 키도 다르다** → 그쪽은 토큰을 키로 써야 세션당 소켓 하나가 된다.
 */
export const USER_SOCKET_KEY = 'user';

export class ViewerSocket {
  /** 키별 공유 인스턴스(로그인 = `USER_SOCKET_KEY` 하나, 익명 = 익명토큰). */
  private static readonly byKey = new Map<string, ViewerSocket>();
  /** WebSocket 기준 URL(예: `wss://rsp365.com`) — rtc.ts 가 `config.WS_BASE` 로 넣는다. */
  static wsBase = '';

  /**
   * 이 키의 공유 소켓을 얻는다(없거나 이미 닫혔으면 새로 만든다). 사용 전 `acquire()`, 끝나면 `release()`.
   * @param key 공유 단위(위 `USER_SOCKET_KEY` 주석 참조) · @param token 새로 열 때 쓸 접근 토큰
   */
  static shared(key: string, token: string): ViewerSocket {
    let s = ViewerSocket.byKey.get(key);
    if (!s || s.dead) {
      s = new ViewerSocket(key, token);
      ViewerSocket.byKey.set(key, s);
    }
    return s;
  }

  private ws?: WebSocket;
  private ready?: Promise<void>;
  /** 닫혔거나 닫히는 중 — 공유 목록에서 새 사용자에게 주지 않는다. */
  private dead = false;
  /** 사용 중인 세션 수. 0 이 되면 소켓을 닫는다(빈 소켓을 붙잡아 두면 서버에 "온라인 뷰어" 로 남는다). */
  private refs = 0;
  private readonly bySession = new Map<string, Handler>();
  private readonly unclaimed: Array<{ at: number; msg: any }> = [];
  /** 끊김 통지 받는 쪽 — 인자 = 서버가 알린 사유(`FINAL_ERRORS`, 없으면 빈 문자열). */
  private readonly closeListeners = new Set<(reason: string) => void>();
  /** 서버가 준 재접속 표(등록 사용자만 — `onLost` 주석). */
  private resume = '';
  /** 서버가 닫기 전에 알린 "다시 붙지 말 것" 사유(`FINAL_ERRORS`). */
  private finalError = '';

  // 생성자 매개변수 속성을 쓰지 않는다 — Node 타입 제거 모드(단위 시험)가 지원하지 않는다.
  private readonly key: string;
  private readonly token: string;

  private constructor(key: string, token: string) {
    this.key = key;
    this.token = token;
  }

  acquire(): void {
    this.refs += 1;
  }

  /** 세션 하나가 소켓 사용을 끝냈다. 마지막 사용자면 소켓을 닫는다. */
  release(): void {
    this.refs = Math.max(0, this.refs - 1);
    if (this.refs === 0) {
      this.shutdown();
    }
  }

  /** 소켓을 연다(이미 열렸거나 여는 중이면 그 결과를 공유). 서버의 `viewer_online` 을 받으면 준비 완료. */
  open(): Promise<void> {
    if (!this.ready) {
      this.ready = this.connect(`token=${encodeURIComponent(this.token)}`).catch((e) => {
        this.die(false); // 처음 연결부터 실패 — 공유 목록에서 빼서 다음 세션은 새로 연다
        throw e;
      });
    }
    return this.ready;
  }

  /**
   * 서버에 붙는다. `viewer_online` 을 받으면 resolve, 그 전에 닫히면 reject.
   * 준비된 뒤 끊기면 `onLost` 가 다시 붙기를 시도한다.
   * @param query `token=…`(처음·익명) 또는 `resume=…`(재접속 표)
   */
  private connect(query: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${ViewerSocket.wsBase}/ws/viewer?${query}`);
      this.ws = ws;
      let online = false;
      ws.onerror = () => {
        if (!online) {
          reject(new Error('WebSocket 오류'));
        }
      };
      ws.onclose = () => {
        if (this.ws === ws) {
          this.ws = undefined;
        }
        if (!online) {
          reject(new Error(this.finalError ? `서버가 연결을 거부했습니다(${this.finalError})` : '서버 연결이 끊겼습니다'));
          return;
        }
        this.onLost();
      };
      ws.onmessage = (ev) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg?.type === MSG_VIEWER_ONLINE) {
          online = true;
          this.takeResume(msg.resume);
          resolve();
          return;
        }
        if (msg?.type === MSG_VIEWER_RESUME) {
          this.takeResume(msg.resume);
          return;
        }
        // 서버가 사유를 알리고 닫는 경우(계정 차단·비밀번호 초기화·폭주) — 이 끊김은 다시 붙으면 안 된다.
        if (msg?.type === MSG_ERROR && FINAL_ERRORS.has(msg.message)) {
          this.finalError = msg.message;
        }
        this.dispatch(msg);
      };
    });
  }

  /** 서버가 준 재접속 표를 기억한다(메모리에만 — 로그에 남기지 않는다). 익명 접속은 표가 없다. */
  private takeResume(v: unknown): void {
    if (typeof v === 'string' && v) {
      this.resume = v;
    }
  }

  /**
   * 준비된 소켓이 끊겼다 → **다시 붙어 본다**(2026-10-20).
   *
   * 신호 소켓이 끊겨도 화면(P2P)은 살아 있다. 서버는 끊긴 뒤 유예(등록 30초·익명 15초) 안에 다시 붙어
   * 세션을 다시 bind 하면 세션을 정리하지 않는다 → 서버 재시작(배포)·순간 끊김에도 원격 세션이 이어진다.
   * 등록 사용자는 재접속 표(`resume`)로, 익명은 처음 토큰(2시간)으로 붙는다. 접근 토큰(15분)은 이미 만료됐을 수 있다.
   *
   * 다시 붙지 않는 경우: 의도한 종료(`shutdown`) · 서버가 사유를 알리고 닫음(`FINAL_ERRORS`) · 시도 창(`RECONNECT_WINDOW_MS`)이 지남.
   * 그때는 종전처럼 주인들에게 알린다(각 RtcClient 가 "서버 연결이 끊겼습니다" 로 탭에 표시).
   */
  private onLost(): void {
    if (this.dead) {
      return; // 의도한 종료
    }
    if (this.finalError) {
      this.die(true);
      return;
    }
    console.log('[rtc] /ws/viewer 끊김 — 다시 붙는 중');
    void this.reconnect();
  }

  private async reconnect(): Promise<void> {
    const deadline = Date.now() + RECONNECT_WINDOW_MS;
    for (let i = 0; Date.now() < deadline; i += 1) {
      await new Promise((r) => setTimeout(r, RECONNECT_DELAYS_MS[Math.min(i, RECONNECT_DELAYS_MS.length - 1)]));
      if (this.dead) {
        return; // 기다리는 사이 세션이 모두 끝났다
      }
      const query = this.resume ? `resume=${encodeURIComponent(this.resume)}` : `token=${encodeURIComponent(this.token)}`;
      try {
        await this.connect(query);
      } catch {
        if (this.finalError) {
          break; // 표·토큰이 거부됐다(차단·만료) — 더 시도해도 같다
        }
        continue;
      }
      if (this.dead) {
        this.ws?.close(); // 붙는 사이 마지막 세션이 끝났다
        return;
      }
      // 이 창이 나르던 세션을 다시 알린다 — 서버가 유예 끝에 "이 세션을 나르는 소켓이 있는가" 로 판정한다.
      for (const sid of this.bySession.keys()) {
        this.send({ type: SESSION_BIND_TYPE, sessionId: sid });
      }
      console.log(`[rtc] /ws/viewer 다시 붙음(세션 ${this.bySession.size}개 다시 알림)`);
      return;
    }
    this.die(true);
  }

  /**
   * 이 소켓을 끝낸다. @param notify true 면 주인들에게 끊김을 알린다(준비된 뒤 끊겨 다시 붙지 못한 경우).
   * 🔴 알리는 이유: 종전엔 로그만 남겨 화면은 "연결됨" 인 채 멈춰 보였다.
   */
  private die(notify: boolean): void {
    this.dead = true;
    if (ViewerSocket.byKey.get(this.key) === this) {
      ViewerSocket.byKey.delete(this.key);
    }
    if (notify) {
      for (const l of [...this.closeListeners]) {
        l(this.finalError);
      }
    }
  }

  /**
   * 이 세션의 메시지를 받겠다고 등록한다. 등록 전에 도착해 보관 중이던 같은 세션 메시지를 **즉시 넘겨준다**.
   * @returns 등록 해제 함수
   */
  claim(sessionId: string, handler: Handler): () => void {
    this.bySession.set(sessionId, handler);
    // 서버에 "이 소켓(창)이 이 세션을 나른다" 고 알린다 — 사용자당 소켓이 창마다 하나씩이라, 이 창이 닫히면
    // 서버가 **이 창의 세션만** 정리하게 한다(다른 창 세션은 그대로). 구 서버는 unknown_type 오류로 답할 뿐 무해하다.
    this.send({ type: SESSION_BIND_TYPE, sessionId });
    const now = Date.now();
    const mine = this.unclaimed.filter((u) => u.msg?.sessionId === sessionId && now - u.at < UNCLAIMED_TTL_MS);
    for (let i = this.unclaimed.length - 1; i >= 0; i -= 1) {
      if (this.unclaimed[i].msg?.sessionId === sessionId) {
        this.unclaimed.splice(i, 1);
      }
    }
    for (const u of mine) {
      handler(u.msg);
    }
    return () => {
      if (this.bySession.get(sessionId) === handler) {
        this.bySession.delete(sessionId);
      }
    };
  }

  /** 준비된 뒤 소켓이 끊기면 부를 함수를 등록한다(인자 = 서버 사유, `lostText` 로 문구). @returns 해제 함수 */
  onClose(listener: (reason: string) => void): () => void {
    this.closeListeners.add(listener);
    return () => this.closeListeners.delete(listener);
  }

  send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  /** 서버 메시지를 주인 세션에 넘긴다. 주인이 아직 없으면 보관, sessionId 가 없는 메시지(상태 푸시 등)는 로그만. */
  private dispatch(msg: any): void {
    const sid = typeof msg?.sessionId === 'string' ? msg.sessionId : '';
    if (!sid) {
      console.log('[rtc] 세션 무관 메시지', msg?.type, msg?.message ?? '');
      return;
    }
    const h = this.bySession.get(sid);
    if (h) {
      h(msg);
      return;
    }
    this.unclaimed.push({ at: Date.now(), msg });
    if (this.unclaimed.length > UNCLAIMED_MAX) {
      this.unclaimed.shift();
    }
  }

  private shutdown(): void {
    this.dead = true;
    if (ViewerSocket.byKey.get(this.key) === this) {
      ViewerSocket.byKey.delete(this.key);
    }
    this.closeListeners.clear(); // 의도한 종료 — 끊김 통지를 보내지 않는다
    try {
      this.ws?.close();
    } catch {
      /* 무시 */
    }
  }
}
